<template>
  <div class="container-fluid py-4">
    <!-- Header -->
    <div class="row">
      <div class="col-12">
        <div class="card my-4 shadow-sm border-0">
          <!-- CAP band -->
          <div class="card-header cap-header">
            <h6 class="text-white mb-0">
              {{ $t('loanRequest.formTitle') }}
            </h6>
          </div>

          <div class="card-body px-4 pt-4 pb-3">
            <form @submit.prevent="submitLoan">
              <div class="row g-3">
                <!-- Amount -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold">{{ $t('loanRequest.form.amount') }} *</label>
               
                    <input
                      type="number"
                      min="100"
                      step="0.01"
                      v-model.number="form.amount"
                      class="form-control custom-border"
                      :placeholder="$t('loanRequest.form.amountPlaceholder')"
                      required
                    />
               
                  <div class="form-text">{{ $t('loanRequest.form.minAmount') }}</div>
                </div>

                <!-- Type -->
                <div class="col-md-6">
                  <label class="form-label fw-semibold">{{ $t('loanRequest.form.type') }} *</label>
                  <select v-model="form.loan_type" class="form-control custom-border" required>
                    <option value="cash">{{ $t('loanRequest.form.types.cash') }}</option>
                    <option value="bank">{{ $t('loanRequest.form.types.bank') }}</option>
                    <option value="other">{{ $t('loanRequest.form.types.other') }}</option>
                  </select>
                </div>
                <div class="col-md-1"></div>

                <!-- Reason -->
                <div class="col-12 mb-3">
                  <label class="form-label fw-semibold">
                    {{ $t('loanRequest.form.reason') }}
                  </label>
                  <div class="border rounded p-2">
                    <textarea
                      v-model="form.reason"
                      class="form-control border-0"
                      rows="4"
                      :placeholder="$t('loanRequest.form.reasonPlaceholder')"
                      style="resize: vertical;"
                    ></textarea>
                  </div>
                </div>

                <!-- Submit -->
                <div class="col-12 text-end">
                  <button
                    type="submit"
                    class="btn text-white"
                    style="background-color:#b03a45;"
                    :disabled="submitting"
                  >
                    <i v-if="submitting" class="fas fa-spinner fa-spin me-1"></i>
                    {{ submitting ? $t('loanRequest.submitting') : $t('loanRequest.submit') }}
                  </button>
                </div>
              </div>
            </form>

            <!-- My loans list -->
            <div v-if="loadingLoans" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">{{ $t('loanRequest.loading') }}</span>
                </div>
              </div>

              <div v-else-if="myLoans.length === 0" class="text-center py-4 text-muted">
                {{ $t('loanRequest.noLoans') }}
              </div>

              <div v-else class="table-responsive mt-2">
                <table class="table table-striped align-middle">
                  <thead>
                    <tr>
                      <th>{{ $t('loanRequest.table.amount') }}</th>
                      <th>{{ $t('loanRequest.table.type') }}</th>
                      <th>{{ $t('loanRequest.table.status') }}</th>
                      <th>{{ $t('loanRequest.table.reason') }}</th>
                      <th>{{ $t('loanRequest.table.history') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in myLoans" :key="row.loan_id">
                      <td>{{ row.total_amount }} TND</td>
                      <td>
                        <span class="badge bg-pink text-white">Loan</span>
                      </td>
                      <td>
                        <span class="badge"
                              :class="row.status==='Approved' ? 'bg-success' :
                                      row.status==='Rejected' ? 'bg-danger' : 'bg-warning'">
                          {{ row.status }}
                        </span>
                      </td>
                      <td class="text-truncate" style="max-width:300px" :title="row.reason">
                        {{ row.reason || '—' }}
                      </td>
                      <td>
                        <button
                          v-if="row.status === 'Approved' && row.payments_history && row.payments_history.length > 0"
                          class="btn btn-info btn-sm"
                          @click="showPaymentHistory(row)"
                          title="View payment history"
                        >
                          📋 History
                        </button>
                        <span v-else class="text-muted small">
                          No history
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <!-- /table -->
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment History Modal -->
    <div v-if="showHistoryModal" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              📋 Payment History
            </h5>
            <button type="button" class="btn-close" @click="closeHistoryModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <strong>Loan Amount:</strong> 
              {{ selectedLoan?.total_amount }} TND
            </div>
            <div class="mb-3">
              <strong>Remaining Amount:</strong> 
              {{ selectedLoan?.remaining_amount }} TND
            </div>
            <div v-if="paymentHistory.length === 0" class="text-center text-muted py-3">
              No payments recorded
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>Payment Date</th>
                    <th>Amount Paid</th>
                    <th>Remaining After Payment</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="payment in paymentHistory" :key="payment.payment_date">
                    <td>{{ formatDate(payment.payment_date) }}</td>
                    <td>{{ payment.amount_paid }} TND</td>
                    <td>{{ payment.remaining_after_payment }} TND</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeHistoryModal">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from '@/api'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const toast = useToast()

/* ===================== STATE ===================== */
const myLoans = ref([])
const loadingLoans = ref(false)
const submitting = ref(false)
const showHistoryModal = ref(false)
const selectedLoan = ref(null)
const paymentHistory = ref([])

const form = ref({
  amount: '',
  loan_type: 'cash',
  reason: ''
})

/* ===================== HELPERS ===================== */
const formatDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-GB') : '—'

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('fr-FR')
}

/* ===================== API ===================== */
async function fetchMyLoans () {
  loadingLoans.value = true
  try {
    const { data } = await axios.get('/employee/loans/me')
    console.log('Response from /employee/loans/me:', data)
    myLoans.value = Array.isArray(data) ? data : []
    console.log('My loans set to:', myLoans.value)
  } catch (e) {
    console.error('Error fetching my loans:', e)
    toast.error(t('loanRequest.messages.loadError'))
  } finally {
    loadingLoans.value = false
  }
}

async function submitLoan () {
  if (!form.value.amount || form.value.amount < 100) {
    toast.error(t('loanRequest.messages.minAmountError'))
    return
  }

  submitting.value = true
  try {
    await axios.post('/employee/loans', {
      amount: form.value.amount,
      loan_type: form.value.loan_type,
      reason: form.value.reason || null
    })

    toast.success(t('loanRequest.messages.submitSuccess'))
    form.value.amount = ''
    form.value.reason = ''
    await fetchMyLoans()
  } catch (e) {
    console.error(e)
    toast.error(t('loanRequest.messages.submitFail'))
  } finally {
    submitting.value = false
  }
}

function showPaymentHistory (loan) {
  console.log('Opening payment history for loan:', loan)
  selectedLoan.value = loan
  paymentHistory.value = loan.payments_history || []
  showHistoryModal.value = true
}

function closeHistoryModal () {
  showHistoryModal.value = false
  selectedLoan.value = null
  paymentHistory.value = []
}

/* ===================== INIT ===================== */
onMounted(fetchMyLoans)
</script>

<style scoped>
.cap-header {
  background: linear-gradient(195deg,#b03a45,#8d2e38);
  padding: 1rem 1.25rem;
  border-radius: 0.5rem 0.5rem 0 0; 
  box-shadow: none;
}

.form-control, .form-select {
  padding:.6rem 1rem;
  border-color:#e9ecef;
}

/* Uniformiser la hauteur et le cadre des champs amount et type */
.col-md-4 .form-control, .col-md-3 .form-select {
  height: 3.5rem; /* Hauteur explicite pour garantir uniformité */
  padding: .6rem 1rem; /* Assurez-vous d'avoir une marge interne constante */
  margin: 0; /* Enlever toute marge interne ou externe supplémentaire */
  border: 1px solid #e9ecef; /* Bordure uniforme pour les deux champs */
  border-radius: 0.375rem; /* Bordure arrondie pour un look cohérent */
}

.table th {
  font-size:.875rem;
  text-transform:uppercase;
  letter-spacing:.025em;
}

.table td {
  vertical-align:middle;
}

.badge {
  padding:.35em .65em;
  border-radius:.5rem;
  text-transform:uppercase;
  font-weight:600;
}

.bg-pink {
  background-color:#d63384;
}
.custom-border {
  border: 2px solid #ccc; /* Light gray border */
 
}

.custom-border:focus {
  border: 2px solid #ccc; /* Keep the border color on focus */
  box-shadow: none; /* Remove any shadow on focus */
}
</style>
